import { useParams, Link } from "react-router-dom"
import { useEffect, useState } from "react"
import { fetchWithAuth, getCSRFToken, getUsername } from "../auth";
import { Button, Message, Input, TextArea, Form, FormField, Modal, ModalContent } from "semantic-ui-react";
import { LoadingWrapper } from "../common";
import './Assets.css'
import "../../css/Assets.css"

export const AssetChecklistViewer = (props) => {
    const { id } = useParams()
    const [html, setHtml] = useState('');
    const [error, setError] = useState('');
    const [loaded, setLoaded] = useState(false)
    const [fullAsset, setFullAsset] = useState({})
    const [assetSubtype, setAssetSubtype] = useState('')
    const [date, setDate] = useState(null)
    const [startingHours, setStartingHours] = useState(null)
    const [notes, setNotes] = useState(null)
    const [success, setSuccess] = useState(false)
    const [showingReportModal, setShowingReportModal] = useState(false)

    async function getAssetSubtype() {
        const response = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + `assets/${id}/`, {
            headers: { 'X-CSRFToken': getCSRFToken() },
            credentials: 'include'
        });
        if (!response.ok) {
            setLoaded(true)
            setError("An error occurred.")
            return;
        }
        setError('')
        const data = await response.json();
        setAssetSubtype(data['deviceSubType']['id'])
        setFullAsset(data)
        setNotes(data.notes)
        const newDate = new Date(data['checklistVerifiedOn'])
        setDate(newDate)
    }

    async function getChecklist() {
        const resp = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + `devicesubtypes/${assetSubtype}/checklist/`, {
            headers: { 'X-CSRFToken': getCSRFToken() },
            credentials: 'include'
        })
        if (!resp.ok) {
            setLoaded(true)
            setError("An error occurred.")
            return;
        }
        const respJson = await resp.json()
        if (!respJson['content'] || !('html' in respJson['content'])) {
            setLoaded(true)
            setError("An error occurred. You might have to recreate the subtype.")
            return
        }
        setHtml(respJson['content']['html'])
        setLoaded(true)
    }

    async function completeChecklist() {
        setSuccess(false)
        const updatedAsset = structuredClone(fullAsset)
        delete updatedAsset['status']
        updatedAsset['checklistVerifiedBy'] = getUsername()
        updatedAsset['checklistVerifiedOn'] = new Date()
        if (updatedAsset.operationalHours) {
            if (!startingHours) {
                setError('Starting hours must be filled out and greater than 0')
                window.scrollTo({top: 0, behavior: 'smooth'})
                return
            }
            const startingHourInt = parseInt(startingHours)
            if (!startingHourInt) {
                setError('Starting hours must be a number greater than 0')
                window.scrollTo({top: 0, behavior: 'smooth'})
                return
            }
            if(fullAsset.operationalHours && startingHourInt < fullAsset.operationalHours) {
                setError('Starting hours must be greater than or equal to previous value.')
                window.scrollTo({top: 0, behavior: 'smooth'})
                return
            }
            updatedAsset['operationalHours'] = startingHourInt
        }
        setLoaded(false)
        const resp = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + `assets/${id}/`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': getCSRFToken()
            },
            credentials: 'include',
            body: JSON.stringify(updatedAsset)
        })
        setError('')
        const respJson = await resp.json()
        setFullAsset(respJson)
        setDate(updatedAsset['checklistVerifiedOn'])
        setSuccess(true)
        props.setJustVerified()
        setLoaded(true)
        window.scrollTo({top: 0, behavior: 'smooth'})
    }

    async function incompleteChecklist() {
        setLoaded(false)
        setSuccess(false)
        if (!notes) {
            setError("Notes must be filled in if incompleting a checklist.")
            window.scrollTo({top: 0, behavior: 'smooth'})
            setLoaded(true)
            return
        }
        const updatedAsset = structuredClone(fullAsset)
        delete updatedAsset['status']
        updatedAsset['notes'] = notes
        updatedAsset['checklistVerifiedBy'] = getUsername()
        updatedAsset['checklistVerifiedOn'] = new Date()
        const resp = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + `assets/${id}/`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': getCSRFToken()
            },
            credentials: 'include',
            body: JSON.stringify(updatedAsset)
        })
        setError('')
        const respJson = await resp.json()
        setDate(updatedAsset['checklistVerifiedOn'])
        props.setJustVerified()
        setFullAsset(respJson)
        setSuccess(true)
        setLoaded(true)
        setShowingReportModal(true)
        window.scrollTo({top: 0, behavior: 'smooth'})
    }

    useEffect(() => {
        getAssetSubtype()
    }, [])

    useEffect(() => {
        if (assetSubtype) {
            getChecklist()
        }
    }, [assetSubtype])

    return (
        <>
            <LoadingWrapper loading={!loaded} text="Loading checklist">
                <div className="outer-wrapper">
                    {error && <Message style={{ width: "fit-content" }} negative>{error}</Message>}
                    {success && <Message style={{ width: "fit-content" }} positive>Success</Message>}
                    <div className="inner-wrapper">
                        <div
                            style={{ textWrap: "wrap" }}
                            dangerouslySetInnerHTML={{ __html: html }}
                        />
                        <Form style={{ width: "25rem", margin: "2rem" }}>
                            {fullAsset.operationalHours && <FormField
                                control={Input}
                                label='Starting hours'
                                name='starting hours'
                                required={true}
                                value={startingHours}
                                onChange={(_, data) => setStartingHours(data.value)} />}
                            <FormField
                                control={TextArea}
                                label='Notes'
                                name='notes'
                                value={notes}
                                onChange={(_, data) => setNotes(data.value)}
                            />
                        </Form>
                        <div>
                            <Button style={{ margin: "0.5rem", width: "fit-content" }} onClick={completeChecklist} color="green">Complete</Button>
                            <Button style={{ margin: "0.5rem", width: "fit-content" }} onClick={incompleteChecklist} color="red">Incomplete</Button>
                        </div>
                        <Modal open={showingReportModal} size="mini">
                            <ModalContent>
                                <div style={{display: "flex", flexDirection: "column", alignItems: "center"}}>
                                    If this asset is newly out of service, please submit a <Link to={'../problems/new'}>problem report</Link>
                                    <Button onClick={() => setShowingReportModal(false)} style={{marginTop: "1rem"}}>Close</Button>
                                </div>
                            </ModalContent>
                        </Modal>
                        {fullAsset['checklistVerifiedBy'] ? <div style={{ marginBottom: "2rem" }}>
                            Last verified on {date?.toLocaleString()} by <Link to={'/members/' + fullAsset['checklistVerifiedByFull'].id}>{fullAsset['checklistVerifiedByFull'].name}</Link>
                        </div> : <div style={{ marginBottom: "2rem" }}>This asset has never been verified</div>}
                    </div>
                </div>
            </LoadingWrapper>
        </>
    );

}
